import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Car, Wrench, FileText, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const DemoPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const demoSteps = [
    {
      title: "Demo-Fahrzeug auswählen",
      description: "Wir haben ein Beispiel-Fahrzeug für Sie vorbereitet",
      icon: Car,
      content: (
        <div className="space-y-4">
          <div className="p-6 rounded-xl" style={{ backgroundColor: '#1F2937', border: '2px solid #7CD4FD' }}>
            <div className="flex items-center gap-4 mb-4">
              <Car className="w-12 h-12" style={{ color: '#7CD4FD' }} />
              <div>
                <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>BMW 320i</h3>
                <p style={{ color: '#94A3B8' }}>2.0L Turbo • Baujahr 2020</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span style={{ color: '#94A3B8' }}>VIN:</span>
                <p style={{ color: '#E6EDF3' }}>WBA12345DEMO67890</p>
              </div>
              <div>
                <span style={{ color: '#94A3B8' }}>Kennzeichen:</span>
                <p style={{ color: '#E6EDF3' }}>DEMO-123</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Symptom-Beschreibung",
      description: "So würden Sie ein Problem beschreiben",
      icon: Wrench,
      content: (
        <div className="space-y-4">
          <div className="p-6 rounded-xl" style={{ backgroundColor: '#1F2937' }}>
            <h4 className="font-semibold mb-3" style={{ color: '#E6EDF3' }}>Beispiel-Symptom:</h4>
            <p style={{ color: '#94A3B8', lineHeight: '1.8' }}>
              "Motor startet nicht. Beim Drehen des Zündschlüssels höre ich nur ein Klicken. 
              Die Batterie ist 3 Jahre alt. Problem tritt seit gestern auf."
            </p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#7CD4FD20', border: '1px solid #7CD4FD' }}>
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 mt-0.5" style={{ color: '#7CD4FD' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
                  Tipp für echte Diagnosen:
                </p>
                <p className="text-sm mt-1" style={{ color: '#E6EDF3' }}>
                  Je detaillierter Ihre Beschreibung, desto präziser die KI-Analyse
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "KI-Diagnose",
      description: "Die KI analysiert das Problem und schlägt Lösungen vor",
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="p-6 rounded-xl" style={{ backgroundColor: '#1F2937' }}>
            <h4 className="font-semibold mb-4" style={{ color: '#7CD4FD' }}>
              🤖 Dual-AI-Analyse (Gemini + GPT-4o)
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-1" style={{ color: '#22C55E' }} />
                <div>
                  <p className="font-semibold" style={{ color: '#E6EDF3' }}>Wahrscheinliche Ursache:</p>
                  <p style={{ color: '#94A3B8' }}>Batterie entladen oder defekt</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-1" style={{ color: '#22C55E' }} />
                <div>
                  <p className="font-semibold" style={{ color: '#E6EDF3' }}>Empfohlene Prüfung:</p>
                  <p style={{ color: '#94A3B8' }}>Batteriespannung mit Multimeter messen (Sollwert: 12.6V)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-1" style={{ color: '#22C55E' }} />
                <div>
                  <p className="font-semibold" style={{ color: '#E6EDF3' }}>Zeitaufwand:</p>
                  <p style={{ color: '#94A3B8' }}>ca. 15 Minuten für Diagnose</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#22C55E20', border: '1px solid #22C55E' }}>
            <p className="text-sm" style={{ color: '#22C55E' }}>
              ✅ In der echten Version erhalten Sie detaillierte Schritt-für-Schritt-Anleitungen mit Bildern, Sollwerten und Sicherheitshinweisen!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "PDF-Bericht",
      description: "Professioneller Diagnosebericht zum Download",
      icon: FileText,
      content: (
        <div className="space-y-4">
          <div className="p-6 rounded-xl" style={{ backgroundColor: '#1F2937' }}>
            <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>
              📄 Beispiel-Bericht
            </h4>
            
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold" style={{ color: '#7CD4FD' }}>Enthält:</p>
                <ul className="mt-2 space-y-1" style={{ color: '#94A3B8' }}>
                  <li>• Fahrzeugdaten und Symptombeschreibung</li>
                  <li>• Alle durchgeführten Prüfschritte</li>
                  <li>• Zeitaufwand pro Schritt</li>
                  <li>• Messwerte und Sollwerte</li>
                  <li>• Diagnose-Ergebnis</li>
                  <li>• Reparaturanleitung</li>
                  <li>• Gesamter Zeitaufwand</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#7CD4FD20', border: '1px solid #7CD4FD' }}>
            <p className="text-sm" style={{ color: '#7CD4FD' }}>
              💡 Perfekt für Kundenrechnungen und Dokumentation!
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = demoSteps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#E6EDF3' }}>
            🎮 Demo-Modus
          </h1>
          <p className="text-lg" style={{ color: '#94A3B8' }}>
            Erleben Sie, wie Carcortex-ai funktioniert – ohne echte Diagnose
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {demoSteps.map((step, index) => (
              <div
                key={index}
                className="flex items-center"
                style={{ flex: index < demoSteps.length - 1 ? 1 : 0 }}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    index <= currentStep ? 'scale-110' : ''
                  }`}
                  style={{
                    backgroundColor: index <= currentStep ? '#7CD4FD' : '#1F2937',
                    color: index <= currentStep ? '#0B0F14' : '#94A3B8',
                    border: index === currentStep ? '3px solid #1EA0E6' : 'none'
                  }}
                >
                  {index + 1}
                </div>
                {index < demoSteps.length - 1 && (
                  <div
                    className="h-1 flex-1 mx-2"
                    style={{
                      backgroundColor: index < currentStep ? '#7CD4FD' : '#1F2937'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs" style={{ color: '#94A3B8' }}>
            {demoSteps.map((step, index) => (
              <span key={index} className={index === currentStep ? 'font-bold' : ''}>
                {step.title}
              </span>
            ))}
          </div>
        </div>

        {/* Main Content Card */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#7CD4FD20' }}
                >
                  <Icon className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{currentStepData.title}</h2>
                  <p className="text-sm font-normal" style={{ color: '#94A3B8' }}>
                    {currentStepData.description}
                  </p>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentStepData.content}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {currentStep > 0 && (
                <Button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex-1"
                  style={{
                    backgroundColor: '#1F2937',
                    color: '#E6EDF3'
                  }}
                >
                  ← Zurück
                </Button>
              )}
              
              {currentStep < demoSteps.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="flex-1"
                  style={{
                    backgroundColor: '#1EA0E6',
                    color: '#fff'
                  }}
                >
                  Weiter →
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/signup')}
                  className="flex-1"
                  style={{
                    background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
                    color: '#fff'
                  }}
                >
                  Jetzt starten
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-6 rounded-xl text-center" style={{ backgroundColor: '#1F2937' }}>
          <p style={{ color: '#94A3B8' }}>
            Dies ist eine Demo. Mit einem echten Account erhalten Sie:
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            <span className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#7CD4FD20', color: '#7CD4FD' }}>
              ✓ Echte KI-Diagnosen
            </span>
            <span className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#7CD4FD20', color: '#7CD4FD' }}>
              ✓ Dual-AI-System
            </span>
            <span className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#7CD4FD20', color: '#7CD4FD' }}>
              ✓ PDF-Berichte
            </span>
            <span className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#7CD4FD20', color: '#7CD4FD' }}>
              ✓ File-Uploads
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
